import { Component } from "react"
import PropTypes from "prop-types"

import sluggify from "shared/lib/sluggify"
import CollectionSelectInput from "./collection_select_input"
import { queryFieldAdd, queryFieldUpdate, queryFieldDelete } from "./gql"

const FIELD_TYPES = [
  { value: "text", label: "Text Input" },
  { value: "dropdown", label: "Dropdown list" },
]

class QueryFieldsForm extends Component {
  constructor(props) {
    super(props)
    this.state = {
      queryField: {
        id: props.queryField.id || "",
        label: props.queryField.label || "",
        slug: props.queryField.slug || "",
        helperText: props.queryField.helper_text || "",
        fieldTypeSelector:
          this.fieldTypeSelector(props.queryField.field_type_definition_slug) ||
          "text",
        fieldTypeSlug:
          props.queryField.field_type_definition_slug || "text_raw",
        populator: props.queryField.static_options || [],
        required: props.queryField.required ? "true" : "false",
      },
      submitting: false,
      errors: {},
    }
  }

  fieldTypeSelector(fieldTypeSlug) {
    if (fieldTypeSlug) {
      return fieldTypeSlug.split("_")[0]
    }
  }

  fieldTypeSlugDefaults(fieldTypeSelector) {
    switch (fieldTypeSelector) {
      case "text":
        return "text_raw"
      case "dropdown":
        return "dropdown_static"
      default:
        return "text_raw"
    }
  }
  showFieldTypeUI = () => {
    switch (this.state.queryField.fieldTypeSelector) {
      case "dropdown":
        const protocolUrl = `${this.props.protocolUrl}/fields/${this.state.queryField.slug}/options`
        return (
          <>
            <CollectionSelectInput
              fieldTypeSlug={this.state.queryField.fieldTypeSlug}
              updateField={this.updateQueryField}
              populator={this.state.queryField.populator}
              protocolUrl={this.state.queryField.slug ? protocolUrl : null}
            />
            {this.state.errors.populator && (
              <FormError errorText={this.state.errors.populator} />
            )}
          </>
        )
      default:
        return null
    }
  }

  handleInputChange = event => {
    const { name, value } = event.target
    this.updateQueryField({ [name]: value })
  }

  updateQueryField = attributes => {
    const queryField = { ...this.state.queryField, ...attributes }
    if (attributes.fieldTypeSelector) {
      queryField.fieldTypeSlug = this.fieldTypeSlugDefaults(
        queryField.fieldTypeSelector
      )
    }
    this.setState({ queryField })
  }

  handleLabelBlur = event => {
    const queryField = this.state.queryField
    if (queryField.slug === "" && queryField.label !== "") {
      this.updateQueryField({ slug: sluggify(queryField.label) })
    }
  }

  handleErrors = errors => {
    const errorsToHash = errors.reduce((acc, cur) => {
      acc[cur.attribute] = `${cur.attribute} ${cur.message}`
      return acc
    }, {})
    errorsToHash["slug"] = errorsToHash["name"]
    if (errors && errors.length > 0) {
      this.setState({ errors: errorsToHash })
      throw errors
    } else {
      this.setState({ errors: {} })
    }
  }

  handleSubmit = event => {
    event.preventDefault()
    this.props.create ? this.handleCreate() : this.handleUpdate()
  }

  handleCreate = () => {
    this.setState({ submitting: true })
    return queryFieldAdd(this.props.query.id, this.state.queryField)
      .then(({ query_field, errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        // segment
        this.props.updateQueryFields("add", query_field)
        this.props.hideForm()
        window.appendFlash("Query field created")
      })
      .catch(errs => {
        window.appendFlash(
          "Could not create query field — please check your data",
          "danger"
        )
        this.setState({ submitting: false })
      })
  }

  handleUpdate = () => {
    this.setState({ submitting: true })
    return queryFieldUpdate(this.state.queryField)
      .then(({ query_field, errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        // segment
        this.props.updateQueryFields("update", query_field)
        this.props.hideForm()
        window.appendFlash("Query field updated")
      })
      .catch(errs => {
        window.appendFlash(
          "Could not update query field — please check your data",
          "danger"
        )
        this.setState({ submitting: false })
      })
  }

  handleDelete = () => {
    this.setState({ submitting: true })
    return queryFieldDelete(this.state.queryField)
      .then(({ errors }) => {
        this.setState({ submitting: false })
        this.handleErrors(errors)
        // segment
        this.props.hideForm()
        this.props.updateQueryFields("delete", this.state.queryField)
        window.appendFlash("Query field deleted")
      })
      .catch(errs => {
        this.setState({ submitting: false })
        window.appendFlash(
          "Could not delete query field — please check your data",
          "danger"
        )
      })
  }

  handleCancelClick = event => {
    event.preventDefault()
    this.props.hideForm()
  }

  confirmDelete = event => {
    event.preventDefault()
    if (
      window.confirm(
        "Are you sure you want to PERMANENTLY delete this query field? Any Applets that use this query field will also be deleted."
      )
    ) {
      return this.handleDelete()
    }
  }

  render() {
    return (
      <>
        <form className="service-edit form" onSubmit={this.handleSubmit}>
          <section>
            <div className="list-item-form">
              <div className="field">
                <div className="label">
                  <label htmlFor="label">Label</label>
                </div>
                <div className="input">
                  <input
                    type="text"
                    name="label"
                    id="label"
                    value={this.state.queryField.label}
                    onBlur={this.handleLabelBlur}
                    onChange={this.handleInputChange}
                    required
                  />
                </div>

                <p className="note">
                  Include a leading statement — use a question where possible
                </p>
                {this.state.errors.label && (
                  <FormError errorText={this.state.errors.label} />
                )}
              </div>

              <div className="field">
                <div className="label">
                  <label htmlFor="helperText">Optional helper text</label>
                </div>
                <div className="input">
                  <input
                    type="text"
                    name="helperText"
                    id="helperText"
                    value={this.state.queryField.helperText}
                    onChange={this.handleInputChange}
                    maxLength={200}
                  />
                </div>
                <p className="note">
                  Markdown-style links allowed. Maximum 200 characters.
                </p>
              </div>

              <div className="field">
                <div className="label">
                  <label htmlFor="slug">Key name</label>
                </div>
                <div className="input">
                  <input
                    type="text"
                    name="slug"
                    id="slug"
                    value={this.state.queryField.slug}
                    minLength={4}
                    pattern="^[a-zA-Z0-9-_]+$"
                    onChange={this.handleInputChange}
                    required
                  />
                </div>

                <p className="note">In snake_case</p>
                {this.state.errors.slug && (
                  <FormError errorText={this.state.errors.slug} />
                )}
              </div>

              <div className="field">
                <div className="label">
                  <label>Required</label>
                </div>
                <div className="input">
                  <div className="input-group">
                    <input
                      type="radio"
                      name="required"
                      id="required"
                      value="true"
                      checked={this.state.queryField.required === "true"}
                      onChange={this.handleInputChange}
                    />
                    <label htmlFor="required">
                      This query field is required.
                    </label>
                  </div>
                  <div className="input-group">
                    <input
                      type="radio"
                      name="required"
                      id="not_required"
                      value="false"
                      checked={this.state.queryField.required === "false"}
                      onChange={this.handleInputChange}
                    />
                    <label htmlFor="not_required">
                      This query field is optional.
                    </label>
                  </div>
                </div>
              </div>

              <div className="field">
                <div className="label">
                  <label>Input Type</label>
                </div>
                <div className="input">
                  <select
                    name="fieldTypeSelector"
                    value={this.state.queryField.fieldTypeSelector}
                    onChange={this.handleInputChange}
                  >
                    {FIELD_TYPES.map(i => (
                      <option key={i.value} value={i.value}>
                        {i.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {this.showFieldTypeUI()}

              <div className="actions">
                <div className="action-left">
                  {this.props.queryField.id &&
                    !this.props.service.is_published && (
                      <a
                        href=""
                        className="secondary-action delete"
                        onClick={this.confirmDelete}
                      >
                        Delete query field
                      </a>
                    )}
                </div>
                <div className="action-right">
                  <a
                    href=""
                    className="secondary-action"
                    onClick={this.handleCancelClick}
                  >
                    Cancel
                  </a>
                  <button
                    type="submit"
                    className="button-secondary"
                    disabled={
                      this.props.service.is_published || this.state.submitting
                    }
                  >
                    {this.state.submitting ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </form>
      </>
    )
  }
}

const FormError = ({ errorText }) => (
  <span className="error-message">Error: {errorText}</span>
)

QueryFieldsForm.propTypes = {
  service: PropTypes.object.isRequired,
  protocolUrl: PropTypes.string.isRequired,
  updateQueryFields: PropTypes.func.isRequired,
  hideForm: PropTypes.func.isRequired,
  query: PropTypes.object.isRequired,
  queryField: PropTypes.object,
  create: PropTypes.bool,
}

QueryFieldsForm.defaultProps = {
  create: false,
  queryField: {},
}

export default QueryFieldsForm
